import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import dayjs from 'dayjs';
import {
  ArrowLeft, Send, X, AtSign, Users, Paperclip, Download,
  MoreHorizontal, Reply, Pencil, Trash2, Check, FileText, Film, Music,
  ChevronLeft, ChevronRight, User as UserIcon, UserPlus, Search, LogOut, Shield,
} from 'lucide-react';
import Loader from '@/components/Loader';
import ConfirmDialog from '@/components/ConfirmDialog';
import Modal from '@/components/Modal';
import { conversationService, userService, fileUrl } from '@/services';
import { useAuth } from '@/hooks/useAuth';
import { fmtDateTime } from '@/utils/format';

function escapeHtml(s = '') {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function buildInputHTML(text, pickedUsers) {
  // No padding/margin on these spans — the highlight must NOT change the text's
  // horizontal width, otherwise the caret drifts away from the typed character.
  const needles = pickedUsers
    .map((u) => ({ str: `@${u.name}`, cls: 'rounded bg-brand-100 text-brand-700 dark:bg-brand-500/25 dark:text-brand-300' }))
    .sort((a, b) => b.str.length - a.str.length);
  const claimed = new Array(text.length).fill(false);
  const matches = [];
  for (const n of needles) {
    let i = 0;
    while ((i = text.indexOf(n.str, i)) !== -1) {
      let conflict = false;
      for (let k = i; k < i + n.str.length; k++) if (claimed[k]) { conflict = true; break; }
      if (!conflict) {
        for (let k = i; k < i + n.str.length; k++) claimed[k] = true;
        matches.push({ idx: i, len: n.str.length, cls: n.cls });
      }
      i += n.str.length;
    }
  }
  matches.sort((a, b) => a.idx - b.idx);
  let html = '';
  let cursor = 0;
  for (const m of matches) {
    if (m.idx > cursor) html += escapeHtml(text.slice(cursor, m.idx));
    html += `<span class="${m.cls}">${escapeHtml(text.slice(m.idx, m.idx + m.len))}</span>`;
    cursor = m.idx + m.len;
  }
  if (cursor < text.length) html += escapeHtml(text.slice(cursor));
  if (text.endsWith('\n')) html += '&nbsp;';
  return html;
}

function resolveMentions(text, picks) {
  if (!picks?.length) return [];
  const sorted = [...picks].sort((a, b) => b.name.length - a.name.length);
  const claimed = new Array(text.length).fill(false);
  const found = new Set();
  for (const p of sorted) {
    const needle = `@${p.name}`;
    let i = 0;
    while ((i = text.indexOf(needle, i)) !== -1) {
      let conflict = false;
      for (let k = i; k < i + needle.length; k++) if (claimed[k]) { conflict = true; break; }
      if (!conflict) {
        for (let k = i; k < i + needle.length; k++) claimed[k] = true;
        found.add(p.id);
      }
      i += needle.length;
    }
  }
  return Array.from(found);
}

function bytesToHuman(n = 0) {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
}

function classifyFile(file) {
  const m = (file.type || '').toLowerCase();
  if (m.startsWith('image/')) return 'image';
  if (m.startsWith('video/')) return 'video';
  if (m.startsWith('audio/')) return 'audio';
  return 'file';
}

// Match http(s) and bare www. URLs; strip trailing punctuation when rendering.
const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<>]+/gi;

function pushLinkified(out, str, keyPrefix, linkClass) {
  if (!str) return;
  const re = new RegExp(URL_RE.source, URL_RE.flags);
  let lastIndex = 0;
  let match;
  let i = 0;
  while ((match = re.exec(str)) !== null) {
    if (match.index > lastIndex) {
      out.push(<span key={`${keyPrefix}-${i++}`}>{str.slice(lastIndex, match.index)}</span>);
    }
    let url = match[0];
    const trail = url.match(/[.,!?;:)\]}'"]+$/);
    if (trail) url = url.slice(0, url.length - trail[0].length);
    const href = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    out.push(
      <a
        key={`${keyPrefix}-${i++}`}
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className={linkClass}
        onClick={(e) => e.stopPropagation()}
      >
        {url}
      </a>
    );
    if (trail) {
      out.push(<span key={`${keyPrefix}-${i++}`}>{trail[0]}</span>);
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < str.length) {
    out.push(<span key={`${keyPrefix}-${i++}`}>{str.slice(lastIndex)}</span>);
  }
}

export default function ChatRoom() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const qc = useQueryClient();

  const convo = useQuery({ queryKey: ['conversation', id], queryFn: () => conversationService.get(id) });
  const messages = useQuery({
    queryKey: ['conversation-messages', id],
    queryFn: () => conversationService.listMessages(id, { limit: 200 }),
    refetchInterval: 2000,
    enabled: !!id,
  });

  // Tell the server we're viewing this chat so it skips notifying us for it.
  useEffect(() => {
    if (!id) return undefined;
    let stopped = false;
    const ping = () => conversationService.heartbeat(id).catch(() => {});
    ping();
    const timer = setInterval(() => {
      if (!stopped && document.visibilityState === 'visible') ping();
    }, 25000);
    return () => {
      stopped = true;
      clearInterval(timer);
      conversationService.clearPresence().catch(() => {});
    };
  }, [id]);

  const c = convo.data?.conversation;
  const isGroup = c?.type === 'group';
  const memberList = c?.members || [];
  const other = !isGroup && c ? memberList.find((m) => String(m._id) !== String(user?._id)) : null;
  const displayName = isGroup ? c?.name : other?.name || 'Chat';
  const subtitle = isGroup
    ? `${memberList.length} members`
    : (other?.designation || other?.email || '');

  const msgItems = messages.data?.items || [];

  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [trigger, setTrigger] = useState({ active: false, query: '', start: -1 });
  const [highlight, setHighlight] = useState(0);
  const [pickedUsers, setPickedUsers] = useState([]);
  const [files, setFiles] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [confirmState, setConfirmState] = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  const [unreadAnchorId, setUnreadAnchorId] = useState(undefined);

  const taRef = useRef(null);
  const ghostRef = useRef(null);
  const listRef = useRef(null);
  const fileInputRef = useRef(null);
  const lastReadRef = useRef(null);
  const didMarkReadRef = useRef(false);

  // Reset read-tracking when switching to a different conversation.
  useEffect(() => {
    lastReadRef.current = null;
    didMarkReadRef.current = false;
    setUnreadAnchorId(undefined);
  }, [id]);

  // Capture my last-read time once (before we mark this chat read below).
  useEffect(() => {
    if (!c || lastReadRef.current !== null) return;
    const mine = (c.reads || []).find((r) => String(r.user) === String(user?._id));
    lastReadRef.current = mine?.at ? new Date(mine.at).getTime() : 0;
  }, [c, user?._id]);

  // Place the "unread" divider before the first incoming message I hadn't read.
  useEffect(() => {
    if (unreadAnchorId !== undefined || lastReadRef.current === null || !msgItems.length) return;
    const lr = lastReadRef.current;
    const first = msgItems.find(
      (m) => new Date(m.createdAt).getTime() > lr && String(m.sender?._id) !== String(user?._id)
    );
    setUnreadAnchorId(first ? first._id : null);
  }, [msgItems, unreadAnchorId, user?._id]);

  // Once messages are loaded, mark the chat read and clear its OS notification.
  useEffect(() => {
    if (!id || didMarkReadRef.current || !msgItems.length || lastReadRef.current === null) return;
    didMarkReadRef.current = true;
    conversationService
      .markRead(id)
      .then(() => {
        qc.invalidateQueries({ queryKey: ['notifications'] });
        qc.invalidateQueries({ queryKey: ['conversations'] });
      })
      .catch(() => {});
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistration('/firebase-messaging-sw.js')
        .then((reg) => {
          if (reg?.getNotifications) {
            reg.getNotifications({ tag: String(id) }).then((ns) => ns.forEach((n) => n.close())).catch(() => {});
          }
        })
        .catch(() => {});
    }
  }, [id, msgItems.length, qc]);

  const detectTrigger = (value, cursor) => {
    const before = value.slice(0, cursor);
    const idx = before.lastIndexOf('@');
    if (idx < 0) return { active: false, query: '', start: -1 };
    const between = before.slice(idx + 1);
    if (between.startsWith('[')) return { active: false, query: '', start: -1 };
    const charBefore = idx === 0 ? ' ' : before[idx - 1];
    if (charBefore && /\w/.test(charBefore)) return { active: false, query: '', start: -1 };
    if (/\s/.test(between) || between.length > 30) return { active: false, query: '', start: -1 };
    return { active: true, query: between.toLowerCase(), start: idx };
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setText(value);
    const cursor = e.target.selectionStart || 0;
    setTrigger(detectTrigger(value, cursor));
    setHighlight(0);
  };

  const insertMention = (member) => {
    const start = trigger.start;
    if (start < 0) return;
    const before = text.slice(0, start);
    const cursor = taRef.current?.selectionStart ?? text.length;
    const after = text.slice(cursor);
    const display = `@${member.name}`;
    setPickedUsers((arr) => (arr.some((u) => u.id === member._id) ? arr : [...arr, { id: member._id, name: member.name }]));
    const next = `${before}${display} ${after}`;
    setText(next);
    setTrigger({ active: false, query: '', start: -1 });
    requestAnimationFrame(() => {
      taRef.current?.focus();
      const pos = (before + display + ' ').length;
      taRef.current?.setSelectionRange(pos, pos);
    });
  };

  const onPickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) return;
    const next = list.map((file) => ({
      file,
      kind: classifyFile(file),
      previewUrl: file.type.startsWith('image/') || file.type.startsWith('video/')
        ? URL.createObjectURL(file)
        : null,
    }));
    setFiles((arr) => [...arr, ...next]);
    e.target.value = '';
  };

  const removeFile = (index) => {
    setFiles((arr) => {
      const removed = arr[index];
      if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
      return arr.filter((_, i) => i !== index);
    });
  };

  useEffect(() => () => {
    files.forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const send = async () => {
    const value = text.trim();
    if ((!value && files.length === 0) || sending) return;

    if (editingId) {
      setSending(true);
      try {
        await conversationService.editMessage(id, editingId, value);
        setEditingId(null);
        setText('');
        setPickedUsers([]);
        await messages.refetch();
      } catch (e) {
        toast.error(e.response?.data?.message || 'Failed');
      } finally {
        setSending(false);
      }
      return;
    }

    const mentions = resolveMentions(value, pickedUsers);
    const fd = new FormData();
    fd.append('text', value);
    mentions.forEach((m) => fd.append('mentions', m));
    if (replyTo) fd.append('replyTo', replyTo._id);
    files.forEach((f) => fd.append('attachments', f.file));

    setSending(true);
    try {
      await conversationService.sendMessage(id, fd);
      files.forEach((f) => f.previewUrl && URL.revokeObjectURL(f.previewUrl));
      setFiles([]);
      setText('');
      setPickedUsers([]);
      setReplyTo(null);
      const fresh = await messages.refetch();
      qc.setQueryData(['conversation-messages', id], fresh.data);
      qc.invalidateQueries({ queryKey: ['conversations'] });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    } finally {
      setSending(false);
    }
  };

  const startEdit = (m) => { setEditingId(m._id); setText(m.text || ''); setReplyTo(null); requestAnimationFrame(() => taRef.current?.focus()); };
  const cancelEdit = () => { setEditingId(null); setText(''); };
  const startReply = (m) => { setReplyTo(m); setEditingId(null); requestAnimationFrame(() => taRef.current?.focus()); };
  const remove = (mid) => {
    setConfirmState({
      title: 'Delete message',
      message: 'This message will be permanently removed from the chat. Continue?',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          await conversationService.deleteMessage(id, mid);
          messages.refetch();
        } catch (e) {
          toast.error(e.response?.data?.message || 'Failed');
        }
      },
    });
  };

  const mentionCandidates = useMemo(() => {
    if (!trigger.active) return [];
    const q = trigger.query;
    return memberList
      .filter((m) => String(m._id) !== String(user?._id))
      .filter((m) => !q || m.name?.toLowerCase().includes(q) || m.email?.toLowerCase().includes(q))
      .slice(0, 8);
  }, [trigger, memberList, user?._id]);

  const onKeyDown = (e) => {
    if (trigger.active && mentionCandidates.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => (h + 1) % mentionCandidates.length); return; }
      if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => (h - 1 + mentionCandidates.length) % mentionCandidates.length); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); insertMention(mentionCandidates[highlight]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setTrigger({ active: false, query: '', start: -1 }); return; }
    }
    if (e.key === 'Escape' && (editingId || replyTo)) {
      e.preventDefault();
      if (editingId) cancelEdit();
      if (replyTo) setReplyTo(null);
      return;
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [msgItems.length]);

  const grouped = useMemo(() => {
    const groups = [];
    let current = null;
    for (const m of msgItems) {
      const day = dayjs(m.createdAt).format('YYYY-MM-DD');
      if (!current || current.day !== day) { current = { day, items: [] }; groups.push(current); }
      current.items.push(m);
    }
    return groups;
  }, [msgItems]);

  const inputHTML = useMemo(() => buildInputHTML(text, pickedUsers), [text, pickedUsers]);

  const openLightbox = (msg, attIndex) => {
    const items = (msg.attachments || []).filter((a) => a.kind === 'image' || a.kind === 'video');
    if (!items.length) return;
    const focused = msg.attachments[attIndex];
    const idx = Math.max(0, items.findIndex((it) => it.url === focused?.url));
    setLightbox({ items, index: idx });
  };

  const canDeleteChat = c && (String(c.createdBy?._id || c.createdBy) === String(user?._id) || user?.role === 'admin');
  const deleteChat = () => {
    setConfirmState({
      title: 'Delete chat',
      message: 'This chat and all its messages will be permanently deleted for everyone. Continue?',
      confirmLabel: 'Delete',
      danger: true,
      onConfirm: async () => {
        try {
          await conversationService.remove(id);
          qc.invalidateQueries({ queryKey: ['conversations'] });
          navigate('/chats');
        } catch (e) {
          toast.error(e.response?.data?.message || 'Failed to delete');
        }
      },
    });
  };

  if (convo.isLoading) return <Loader />;
  if (!c) return <div>Chat not found</div>;

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] -m-4 md:-m-6">
      {/* Header */}
      <div className="px-4 md:px-6 py-3 bg-gradient-to-r from-white to-slate-50 dark:from-slate-900 dark:to-slate-900/60 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button onClick={() => navigate('/chats')} className="btn-ghost p-2 rounded-full" title="Back">
            <ArrowLeft size={18} />
          </button>
          <div className={`w-11 h-11 rounded-2xl text-white grid place-items-center font-bold text-lg shadow-sm shrink-0 ${
            isGroup ? 'bg-gradient-to-br from-violet-500 to-pink-500' : 'bg-gradient-to-br from-brand-500 to-indigo-600'
          }`}>
            {isGroup ? <Users size={20} /> : (displayName?.[0]?.toUpperCase() || '?')}
          </div>
          <div className="min-w-0">
            <h2 className="font-bold text-lg truncate flex items-center gap-2">
              {displayName}
              <span className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold uppercase tracking-wide bg-brand-50 text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">
                {isGroup ? <Users size={10} /> : <UserIcon size={10} />} {isGroup ? 'Group' : 'Direct'}
              </span>
            </h2>
            <div className="text-xs text-slate-500 truncate">{subtitle}</div>
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isGroup && (
            <button
              onClick={() => setShowMembers(true)}
              title="Manage members"
              className="flex items-center gap-2 rounded-full pl-2 pr-1.5 py-1 hover:bg-slate-100 dark:hover:bg-slate-800 transition shrink-0"
            >
              <div className="hidden md:flex items-center -space-x-2">
                {memberList.slice(0, 5).map((m) => (
                  <div key={m._id} title={m.name} className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500 text-white grid place-items-center text-xs font-semibold ring-2 ring-white dark:ring-slate-900">
                    {m.name?.[0]?.toUpperCase()}
                  </div>
                ))}
                {memberList.length > 5 && (
                  <div className="w-8 h-8 rounded-full bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 grid place-items-center text-xs font-semibold ring-2 ring-white dark:ring-slate-900">
                    +{memberList.length - 5}
                  </div>
                )}
              </div>
              <span className="w-9 h-9 rounded-full bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300 grid place-items-center shrink-0">
                <UserPlus size={16} />
              </span>
            </button>
          )}
          {canDeleteChat && (
            <button
              onClick={deleteChat}
              title="Delete chat"
              className="w-9 h-9 rounded-full grid place-items-center text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition shrink-0"
            >
              <Trash2 size={17} />
            </button>
          )}
        </div>
      </div>

      {/* Messages */}
      <div ref={listRef} className="flex-1 overflow-y-auto px-4 md:px-6 py-4 bg-slate-50 dark:bg-slate-950 space-y-3">
        {messages.isLoading && <Loader />}
        {!messages.isLoading && msgItems.length === 0 && (
          <div className="text-center text-sm text-slate-500 py-16">No messages yet. Say hi 👋</div>
        )}
        {grouped.map((g) => (
          <div key={g.day} className="space-y-2">
            <div className="flex items-center justify-center my-3">
              <span className="px-2 py-0.5 rounded-full text-[11px] bg-slate-200 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                {dayjs(g.day).isSame(dayjs(), 'day') ? 'Today' : dayjs(g.day).format('DD MMM YYYY')}
              </span>
            </div>
            {g.items.map((m) => (
              <div key={m._id}>
                {m._id === unreadAnchorId && (
                  <div className="flex items-center gap-2 my-3">
                    <div className="flex-1 h-px bg-rose-300 dark:bg-rose-500/40" />
                    <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300">
                      Unread messages
                    </span>
                    <div className="flex-1 h-px bg-rose-300 dark:bg-rose-500/40" />
                  </div>
                )}
                <ChatBubble
                  msg={m}
                  own={m.sender?._id === user?._id}
                  showSender={isGroup}
                  currentUserId={user?._id}
                  onDelete={() => remove(m._id)}
                  onEdit={() => startEdit(m)}
                  onReply={() => startReply(m)}
                  onOpenLightbox={(idx) => openLightbox(m, idx)}
                />
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Input */}
      <div className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800">
        {replyTo && (
          <div className="px-4 md:px-6 pt-3">
            <div className="flex items-start justify-between gap-2 px-3 py-2 rounded-lg bg-brand-50 dark:bg-brand-500/10 border-l-4 border-brand-500">
              <div className="min-w-0">
                <div className="text-[11px] font-medium text-brand-700 dark:text-brand-300 flex items-center gap-1">
                  <Reply size={11} /> Replying to {replyTo.sender?.name}
                </div>
                <div className="text-xs text-slate-600 dark:text-slate-300 truncate mt-0.5">
                  {replyTo.text || `${replyTo.attachments?.length || 0} attachment(s)`}
                </div>
              </div>
              <button type="button" onClick={() => setReplyTo(null)} className="text-slate-500 hover:text-rose-500 p-1">
                <X size={14} />
              </button>
            </div>
          </div>
        )}
        {editingId && (
          <div className="px-4 md:px-6 pt-3">
            <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/30 text-xs">
              <div className="flex items-center gap-2 text-amber-700 dark:text-amber-300">
                <Pencil size={12} /> Editing message
              </div>
              <button onClick={cancelEdit} className="text-amber-700 dark:text-amber-300 hover:underline">Cancel</button>
            </div>
          </div>
        )}

        {files.length > 0 && (
          <div className="px-4 md:px-6 pt-3 flex gap-2 flex-wrap">
            {files.map((f, i) => (
              <div key={i} className="relative w-24 h-24 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 overflow-hidden group">
                {f.kind === 'image' && f.previewUrl && <img src={f.previewUrl} alt="" className="w-full h-full object-cover" />}
                {f.kind === 'video' && f.previewUrl && (
                  <>
                    <video src={f.previewUrl} className="w-full h-full object-cover" muted />
                    <div className="absolute inset-0 grid place-items-center bg-black/30 text-white"><Film size={20} /></div>
                  </>
                )}
                {(f.kind === 'audio' || f.kind === 'file') && (
                  <div className="w-full h-full flex flex-col items-center justify-center text-slate-500">
                    {f.kind === 'audio' ? <Music size={20} /> : <FileText size={20} />}
                    <span className="text-[10px] mt-1 truncate w-20 px-1 text-center">{f.file.name}</span>
                  </div>
                )}
                <button type="button" onClick={() => removeFile(i)}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-slate-900/70 text-white grid place-items-center opacity-0 group-hover:opacity-100">
                  <X size={11} />
                </button>
                <div className="absolute bottom-0 inset-x-0 px-1 py-0.5 text-[10px] bg-gradient-to-t from-black/60 to-transparent text-white truncate">
                  {bytesToHuman(f.file.size)}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="relative">
          {trigger.active && mentionCandidates.length > 0 && (
            <div className="absolute bottom-full left-4 md:left-6 mb-2 w-80 border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-900 shadow-soft overflow-hidden z-10">
              <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200 dark:border-slate-800 flex items-center gap-1">
                <AtSign size={11} /> Mention member
              </div>
              <div className="max-h-56 overflow-y-auto">
                {mentionCandidates.map((m, i) => (
                  <button key={m._id} type="button"
                    onMouseDown={(e) => { e.preventDefault(); insertMention(m); }}
                    onMouseEnter={() => setHighlight(i)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-left text-sm ${i === highlight ? 'bg-brand-50 dark:bg-brand-500/10' : 'hover:bg-slate-50 dark:hover:bg-slate-800/60'}`}>
                    <div className="w-7 h-7 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500 text-white grid place-items-center text-xs font-semibold">
                      {m.name?.[0]?.toUpperCase()}
                    </div>
                    <div className="min-w-0">
                      <div className="font-medium truncate">{m.name}</div>
                      <div className="text-[11px] text-slate-500 truncate">{m.email}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="px-4 md:px-6 pb-3 pt-2">
            <div className="relative">
              <div
                ref={ghostRef}
                aria-hidden="true"
                className="input absolute inset-0 min-h-[6rem] max-h-60 overflow-auto pointer-events-none whitespace-pre-wrap break-words bg-transparent border-transparent text-slate-800 dark:text-slate-100 pr-14"
                dangerouslySetInnerHTML={{ __html: inputHTML || '&#8203;' }}
              />
              <textarea
                ref={taRef}
                value={text}
                onChange={handleChange}
                onKeyDown={onKeyDown}
                onScroll={(e) => { if (ghostRef.current) ghostRef.current.scrollTop = e.target.scrollTop; }}
                rows={3}
                placeholder={editingId ? 'Edit your message...' : (isGroup ? 'Message the group... (@ to mention)' : 'Write a message...')}
                className="input resize-none w-full min-h-[6rem] max-h-60 relative bg-transparent text-transparent caret-slate-800 dark:caret-slate-100 selection:bg-brand-200/60 dark:selection:bg-brand-500/30 pr-14"
              />
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept="image/*,video/*,audio/*,application/pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.7z,.csv,.json"
                onChange={onPickFiles}
                className="hidden"
              />
              <div className="absolute bottom-2.5 right-2.5 flex flex-col gap-1.5">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={editingId !== null}
                  title="Attach"
                  className="w-10 h-10 rounded-xl grid place-items-center transition-all bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700 hover:text-brand-600 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <Paperclip size={16} />
                </button>
                <button
                  onClick={send}
                  disabled={sending || (!text.trim() && files.length === 0)}
                  title={editingId ? 'Save (Enter)' : 'Send (Enter)'}
                  className={`w-10 h-10 rounded-xl grid place-items-center transition-all shadow-sm ${
                    sending || (!text.trim() && files.length === 0)
                      ? 'bg-slate-200 dark:bg-slate-700 text-slate-400 cursor-not-allowed'
                      : 'bg-gradient-to-br from-brand-500 to-indigo-600 text-white hover:from-brand-600 hover:to-indigo-700 hover:shadow-md active:scale-95'
                  }`}
                >
                  {sending ? <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> : editingId ? <Check size={16} /> : <Send size={16} />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {lightbox && (
        <Lightbox items={lightbox.items} index={lightbox.index} onClose={() => setLightbox(null)} />
      )}

      {showMembers && isGroup && c && (
        <MembersModal
          conversation={c}
          currentUser={user}
          onClose={() => setShowMembers(false)}
          onChanged={() => convo.refetch()}
          onLeave={() => { setShowMembers(false); navigate('/chats'); }}
        />
      )}

      <ConfirmDialog state={confirmState} onClose={() => setConfirmState(null)} />
    </div>
  );
}

function MembersModal({ conversation, currentUser, onClose, onChanged, onLeave }) {
  const c = conversation;
  const members = c.members || [];
  const adminIds = new Set((c.admins || []).map((a) => String(a._id || a)));
  const isAdmin = adminIds.has(String(currentUser?._id)) || currentUser?.role === 'admin';

  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState([]);
  const [busy, setBusy] = useState(false);

  const users = useQuery({
    queryKey: ['users', 'all', 'chat'],
    queryFn: () => userService.list({ limit: 200 }),
  });

  const candidates = useMemo(() => {
    const memberSet = new Set(members.map((m) => String(m._id)));
    const q = search.trim().toLowerCase();
    return (users.data?.items || [])
      .filter((u) => !memberSet.has(String(u._id)))
      .filter((u) => !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q));
  }, [users.data, search, members]);

  const toggle = (id) => setSelected((arr) => (arr.includes(id) ? arr.filter((x) => x !== id) : [...arr, id]));

  const addSelected = async () => {
    if (!selected.length) return;
    setBusy(true);
    try {
      await conversationService.addMembers(c._id, selected);
      toast.success(`Added ${selected.length} member${selected.length > 1 ? 's' : ''}`);
      setSelected([]);
      await onChanged();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to add members');
    } finally {
      setBusy(false);
    }
  };

  const removeMember = async (userId, isSelf) => {
    setBusy(true);
    try {
      await conversationService.removeMember(c._id, userId);
      if (isSelf) {
        onLeave?.();
      } else {
        await onChanged();
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to remove');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={`Members · ${c.name || 'Group'}`}
      size="lg"
      footer={
        <>
          <button onClick={onClose} className="btn-secondary">Close</button>
          {selected.length > 0 && (
            <button onClick={addSelected} className="btn-primary" disabled={busy}>
              <UserPlus size={16} /> Add {selected.length}
            </button>
          )}
        </>
      }
    >
      {/* Current members */}
      <div className="mb-4">
        <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">
          Current members ({members.length})
        </div>
        <div className="max-h-52 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
          {members.map((m) => {
            const isSelf = String(m._id) === String(currentUser?._id);
            const memberIsAdmin = adminIds.has(String(m._id));
            const canRemove = isSelf || isAdmin;
            return (
              <div key={m._id} className="flex items-center gap-3 p-2.5 text-sm">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500 text-white grid place-items-center font-semibold shrink-0">
                  {m.name?.[0]?.toUpperCase()}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate flex items-center gap-1.5">
                    {m.name}{isSelf && <span className="text-[11px] text-slate-400">(you)</span>}
                    {memberIsAdmin && (
                      <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-amber-600 dark:text-amber-400">
                        <Shield size={10} /> Admin
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-slate-500 truncate">{m.email}</div>
                </div>
                {canRemove && (
                  <button
                    onClick={() => removeMember(m._id, isSelf)}
                    disabled={busy}
                    title={isSelf ? 'Leave group' : 'Remove member'}
                    className="p-1.5 rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 disabled:opacity-40"
                  >
                    {isSelf ? <LogOut size={15} /> : <Trash2 size={15} />}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Add members */}
      <div className="text-xs uppercase tracking-wide text-slate-500 mb-2">Add members</div>
      <div className="relative mb-2">
        <Search size={14} className="absolute left-3 top-3 text-slate-400" />
        <input
          className="input pl-9"
          placeholder="Search teammates..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-800">
        {users.isLoading && <div className="p-4 text-sm text-slate-500 text-center">Loading...</div>}
        {!users.isLoading && candidates.length === 0 && (
          <div className="p-4 text-sm text-slate-500 text-center">Everyone is already in this group</div>
        )}
        {candidates.map((u) => {
          const checked = selected.includes(u._id);
          return (
            <label key={u._id} className="flex items-center gap-3 p-2.5 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 text-sm">
              <input type="checkbox" className="rounded" checked={checked} onChange={() => toggle(u._id)} />
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-500 to-indigo-500 text-white grid place-items-center font-semibold">
                {u.name?.[0]?.toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{u.name}</div>
                <div className="text-[11px] text-slate-500 truncate">{u.email}{u.department ? ` · ${u.department}` : ''}</div>
              </div>
            </label>
          );
        })}
      </div>
    </Modal>
  );
}

function ChatBubble({ msg, own, showSender, currentUserId, onDelete, onEdit, onReply, onOpenLightbox }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const h = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [menuOpen]);

  const mentionedMe = useMemo(
    () => (msg.mentions || []).some((u) => String(u._id || u) === String(currentUserId)),
    [msg.mentions, currentUserId]
  );

  const parts = useMemo(() => renderMentions(msg.text, msg.mentions || [], currentUserId, own), [msg.text, msg.mentions, currentUserId, own]);

  const attachments = msg.attachments || [];
  const images = attachments.filter((a) => a.kind === 'image');
  const videos = attachments.filter((a) => a.kind === 'video');
  const others = attachments.filter((a) => a.kind !== 'image' && a.kind !== 'video');

  return (
    <div className={`group flex items-end gap-2 ${own ? 'flex-row-reverse' : 'flex-row'}`}>
      <div className={`w-8 h-8 shrink-0 rounded-full grid place-items-center text-white text-xs font-semibold ${
        own ? 'bg-gradient-to-br from-brand-500 to-indigo-600' : 'bg-gradient-to-br from-slate-500 to-slate-700'
      }`}>
        {msg.sender?.name?.[0]?.toUpperCase() || '?'}
      </div>
      <div className={`max-w-[75%] ${own ? 'items-end' : 'items-start'} flex flex-col relative`}>
        {!own && showSender && <div className="text-[11px] text-slate-500 mb-0.5 ml-2">{msg.sender?.name}</div>}
        <div className={`relative ${own ? 'ml-auto' : ''}`}>
          <div className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${
            own
              ? 'bg-brand-600 text-white rounded-br-sm'
              : mentionedMe
              ? 'bg-amber-50 dark:bg-amber-500/10 text-slate-800 dark:text-slate-100 border border-amber-200 dark:border-amber-500/30 rounded-bl-sm'
              : 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-bl-sm'
          }`}>
            {msg.replyTo && (
              <div className={`mb-1.5 px-2 py-1.5 rounded-lg border-l-2 text-xs ${
                own ? 'bg-white/15 border-white/50' : 'bg-slate-100 dark:bg-slate-700/50 border-slate-400 dark:border-slate-500'
              }`}>
                <div className={`font-semibold ${own ? 'text-white/90' : 'text-slate-700 dark:text-slate-200'}`}>
                  {msg.replyTo.sender?.name}
                </div>
                <div className={`truncate ${own ? 'text-white/70' : 'text-slate-500 dark:text-slate-400'}`}>
                  {msg.replyTo.text || `${msg.replyTo.attachments?.length || 0} attachment(s)`}
                </div>
              </div>
            )}

            {images.length > 0 && <ImageGrid images={images} attachments={attachments} onOpen={onOpenLightbox} />}
            {videos.length > 0 && (
              <div className={`mt-1.5 flex flex-col gap-1.5 ${images.length === 0 ? '' : 'mt-2'}`}>
                {videos.map((v, i) => (
                  <button key={i} type="button" onClick={() => onOpenLightbox(attachments.indexOf(v))}
                    className="relative block max-w-xs rounded-lg overflow-hidden bg-black">
                    <video src={fileUrl(v.url)} className="w-full max-h-64" muted preload="metadata" />
                    <span className="absolute inset-0 grid place-items-center bg-black/30 hover:bg-black/40">
                      <span className="w-12 h-12 rounded-full bg-white/90 text-slate-900 grid place-items-center"><Film size={20} /></span>
                    </span>
                  </button>
                ))}
              </div>
            )}
            {others.length > 0 && (
              <div className={`flex flex-col gap-1.5 ${(images.length || videos.length || msg.text) ? 'mt-2' : ''}`}>
                {others.map((f, i) => <FileRow key={i} att={f} own={own} />)}
              </div>
            )}

            {msg.text && (attachments.length === 0 ? null : <div className="mt-2" />)}
            {msg.text && <div>{parts}</div>}
          </div>

          <button
            onClick={() => setMenuOpen((v) => !v)}
            className={`absolute -top-2 ${own ? '-left-7' : '-right-7'} w-7 h-7 rounded-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-sm text-slate-500 hover:text-slate-700 dark:hover:text-slate-200 opacity-0 group-hover:opacity-100 transition grid place-items-center`}
          >
            <MoreHorizontal size={14} />
          </button>

          {menuOpen && (
            <div ref={menuRef} className={`absolute z-20 ${own ? 'right-0' : 'left-0'} top-full mt-1 w-40 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 shadow-soft py-1`}>
              <button onClick={() => { setMenuOpen(false); onReply(); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2">
                <Reply size={14} /> Reply
              </button>
              {own && (
                <button onClick={() => { setMenuOpen(false); onEdit(); }} className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 dark:hover:bg-slate-700 flex items-center gap-2">
                  <Pencil size={14} /> Edit
                </button>
              )}
              {own && (
                <button onClick={() => { setMenuOpen(false); onDelete(); }} className="w-full text-left px-3 py-1.5 text-sm text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-500/10 flex items-center gap-2">
                  <Trash2 size={14} /> Delete
                </button>
              )}
            </div>
          )}
        </div>
        <div className="text-[10px] text-slate-400 mt-0.5 px-1 flex items-center gap-1">
          {fmtDateTime(msg.createdAt)}
          {msg.editedAt && <span className="italic">(edited)</span>}
        </div>
      </div>
    </div>
  );
}

function ImageGrid({ images, attachments, onOpen }) {
  const count = images.length;
  if (count === 1) return (
    <button onClick={() => onOpen(attachments.indexOf(images[0]))} className="block mt-1 mb-1 rounded-lg overflow-hidden">
      <img src={fileUrl(images[0].url)} alt={images[0].filename} className="max-w-xs max-h-72 object-cover rounded-lg" />
    </button>
  );
  if (count === 2) return (
    <div className="grid grid-cols-2 gap-1 mt-1 mb-1 max-w-xs">
      {images.map((img, i) => (
        <button key={i} onClick={() => onOpen(attachments.indexOf(img))} className="rounded-lg overflow-hidden aspect-square bg-slate-100 dark:bg-slate-700">
          <img src={fileUrl(img.url)} alt={img.filename} className="w-full h-full object-cover" />
        </button>
      ))}
    </div>
  );
  const extra = count - 2;
  return (
    <div className="grid grid-cols-2 gap-1 mt-1 mb-1 max-w-xs">
      <button onClick={() => onOpen(attachments.indexOf(images[0]))} className="rounded-lg overflow-hidden aspect-square bg-slate-100 dark:bg-slate-700">
        <img src={fileUrl(images[0].url)} alt={images[0].filename} className="w-full h-full object-cover" />
      </button>
      <button onClick={() => onOpen(attachments.indexOf(images[1]))} className="relative rounded-lg overflow-hidden aspect-square bg-slate-100 dark:bg-slate-700">
        <img src={fileUrl(images[1].url)} alt={images[1].filename} className="w-full h-full object-cover" />
        <div className="absolute inset-0 grid place-items-center text-white text-2xl font-bold bg-black/45">+{extra}</div>
      </button>
    </div>
  );
}

function FileRow({ att, own }) {
  return (
    <a href={fileUrl(att.url)} download={att.filename} target="_blank" rel="noreferrer"
      className={`flex items-center gap-2 px-2.5 py-2 rounded-lg max-w-xs ${
        own ? 'bg-white/15 hover:bg-white/25 text-white'
            : 'bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-100'
      }`}>
      <div className={`w-9 h-9 rounded-lg grid place-items-center shrink-0 ${own ? 'bg-white/20' : 'bg-white dark:bg-slate-800 text-slate-500'}`}>
        {att.kind === 'audio' ? <Music size={16} /> : <FileText size={16} />}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{att.filename}</div>
        <div className={`text-[11px] ${own ? 'text-white/70' : 'text-slate-500 dark:text-slate-400'}`}>{bytesToHuman(att.size)}</div>
      </div>
      <Download size={14} className="shrink-0" />
    </a>
  );
}

function Lightbox({ items, index: initial, onClose }) {
  const [index, setIndex] = useState(initial);
  const current = items[index];
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight' && index < items.length - 1) setIndex(index + 1);
      if (e.key === 'ArrowLeft' && index > 0) setIndex(index - 1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, items.length, onClose]);
  if (!current) return null;
  return (
    <div className="fixed inset-0 z-[100] bg-black/85 grid place-items-center p-4 animate-fade-in" onClick={onClose}>
      <button onClick={onClose} className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center">
        <X size={20} />
      </button>
      {items.length > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); setIndex(Math.max(0, index - 1)); }} disabled={index === 0}
            className="absolute left-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center disabled:opacity-30">
            <ChevronLeft size={20} />
          </button>
          <button onClick={(e) => { e.stopPropagation(); setIndex(Math.min(items.length - 1, index + 1)); }} disabled={index === items.length - 1}
            className="absolute right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white grid place-items-center disabled:opacity-30">
            <ChevronRight size={20} />
          </button>
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/80 text-sm">{index + 1} / {items.length}</div>
        </>
      )}
      <div className="max-w-[90vw] max-h-[85vh]" onClick={(e) => e.stopPropagation()}>
        {current.kind === 'image' ? (
          <img src={fileUrl(current.url)} alt={current.filename} className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg" />
        ) : (
          <video src={fileUrl(current.url)} controls autoPlay className="max-w-[90vw] max-h-[85vh] rounded-lg bg-black" />
        )}
      </div>
      <a href={fileUrl(current.url)} download={current.filename} onClick={(e) => e.stopPropagation()}
        className="absolute top-4 right-16 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-white text-sm flex items-center gap-1.5">
        <Download size={14} /> Download
      </a>
    </div>
  );
}

function renderMentions(text, mentions, currentUserId, ownBubble) {
  if (!text) return null;
  const needles = mentions.map((u) => ({ str: `@${u.name}`, user: u })).sort((a, b) => b.str.length - a.str.length);
  const matches = [];
  const claimed = new Array(text.length).fill(false);
  for (const n of needles) {
    let i = 0;
    while ((i = text.indexOf(n.str, i)) !== -1) {
      let conflict = false;
      for (let k = i; k < i + n.str.length; k++) if (claimed[k]) { conflict = true; break; }
      if (!conflict) {
        for (let k = i; k < i + n.str.length; k++) claimed[k] = true;
        matches.push({ idx: i, len: n.str.length, user: n.user });
      }
      i += n.str.length;
    }
  }
  matches.sort((a, b) => a.idx - b.idx);
  const out = [];
  let cursor = 0;
  let key = 0;
  const linkClass = ownBubble
    ? 'underline decoration-white/60 text-white hover:decoration-white'
    : 'underline text-brand-600 dark:text-brand-300 hover:text-brand-700 dark:hover:text-brand-200 break-all';
  for (const m of matches) {
    if (m.idx > cursor) pushLinkified(out, text.slice(cursor, m.idx), `seg-${key++}`, linkClass);
    const isSelf = String(m.user._id || m.user) === String(currentUserId);
    out.push(
      <span key={`m-${key++}`} className={`inline-flex items-center px-1.5 py-0.5 rounded font-medium ${
        ownBubble ? 'bg-white/20 text-white'
                  : isSelf ? 'bg-amber-200 dark:bg-amber-500/30 text-amber-900 dark:text-amber-200'
                           : 'bg-brand-100 dark:bg-brand-500/20 text-brand-700 dark:text-brand-300'
      }`}>
        @{m.user.name}
      </span>
    );
    cursor = m.idx + m.len;
  }
  if (cursor < text.length) pushLinkified(out, text.slice(cursor), `seg-${key++}`, linkClass);
  return out;
}
